/**
 * citrine — integration helpers for a local on-device Nostr relay.
 * ─────────────────────────────────────────────────────────────────────────────
 * Citrine (github.com/greenart7c3/Citrine) is an Android app by the author of
 * Amber that runs a full Nostr relay locally on the phone, storing events in an
 * on-device SQLite database and listening on a localhost WebSocket. Because it
 * is "just a relay URL", it slots into our existing nostr.relay()/group() and
 * useSkateRelays machinery with no new protocol.
 *
 * Why we want it (SkateSpots-specific):
 *  - Offline-in-the-field: read spots/tours/events at a spot with no signal, and
 *    capture writes locally to publish when connectivity returns.
 *  - Instant reads: a localhost query returns in ~1ms vs. 100–800ms remote.
 *  - Resilience/backup: a durable local copy of the user's own events survives
 *    public-relay outages and addressable-event eviction.
 *  - Privacy: local-first event classes (e.g. Stash) can avoid public relays.
 *
 * Hard constraints (must be respected everywhere Citrine is used):
 *  - Android-only. iOS/desktop web get none of this — always platform-gate.
 *  - Opt-in only. Never route data through Citrine without an explicit setting.
 *  - Local ≠ encrypted. Citrine stores whatever we give it verbatim; sensitive
 *    data (Stash, DMs) must stay NIP-44 encrypted regardless of destination.
 */

/** Citrine's default localhost relay endpoint. */
export const CITRINE_RELAY_URL = 'ws://127.0.0.1:4869';

/** Forcible UI states for the dev preview flag. */
export type CitrinePreviewState = 'off' | 'searching' | 'live' | 'waiting';
const PREVIEW_KEY = 'citrine:preview';

/**
 * DEV PREVIEW ONLY — lets the (normally Android-only) Citrine UI render in a
 * browser so the design can be reviewed without an APK build.
 *
 * Set in the browser console:
 *   localStorage.setItem('citrine:preview', '1')        // show the card, real behaviour
 *   localStorage.setItem('citrine:preview', 'live')     // force the "connected" state
 *   localStorage.setItem('citrine:preview', 'waiting')  // force "not detected"
 *   localStorage.setItem('citrine:preview', 'searching')// force "searching…"
 *   localStorage.removeItem('citrine:preview')          // back to normal
 *
 * CRITICAL: this unlocks RENDERING ONLY. It never makes the local relay
 * `active` and never yields a relay `url`, so no query or publish is ever routed
 * anywhere different — production relay behaviour is completely unchanged. A
 * forced state is purely cosmetic.
 */
export function citrinePreview(): CitrinePreviewState | null {
  try {
    const raw = (globalThis as { localStorage?: Storage }).localStorage?.getItem(PREVIEW_KEY);
    if (!raw) return null;
    if (raw === 'off' || raw === 'searching' || raw === 'live' || raw === 'waiting') {
      return raw;
    }
    // Any other truthy value ("1"/"true") = show the card with real behaviour.
    return 'off';
  } catch {
    return null;
  }
}

/** True if the dev preview flag is set at all (any value). */
export function isCitrinePreview(): boolean {
  return citrinePreview() !== null;
}

/**
 * True when running inside a Capacitor native Android shell. Citrine is an
 * Android app, so the local relay can only exist there. This never throws in a
 * plain browser (window.Capacitor is simply undefined).
 */
export function isAndroidNative(): boolean {
  const cap = (globalThis as {
    Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string };
  }).Capacitor;
  if (!cap?.isNativePlatform?.()) return false;
  return cap.getPlatform?.() === 'android';
}

/**
 * True if the Citrine UI/feature should be offered on this device — native
 * Android, OR the dev preview flag is set (browser design review). Actual
 * reachability is still confirmed separately by probeCitrine(), which fails
 * cleanly in a browser, so preview never routes real traffic.
 */
export function isCitrinePossible(): boolean {
  return isAndroidNative() || isCitrinePreview();
}

/**
 * Probe whether a Citrine relay is actually running AND speaking Nostr at `url`.
 *
 * A bare "socket opened" check is not enough: any process squatting on the port
 * would open cleanly and we'd then wrongly route reads — and, worse, published
 * events — at a non-Nostr peer. So we complete a minimal Nostr handshake:
 * open → send a tiny bounded REQ → require a protocol-shaped reply
 * (EVENT / EOSE / NOTICE / CLOSED) referencing our subscription id.
 *
 * Resolves true only on a verified Nostr reply; false on error, timeout, or a
 * non-Nostr response. Never rejects, never throws — safe from a query/effect.
 * Short-circuits to false off-Android (and outside preview) to avoid pointless
 * localhost attempts and console noise in the browser/desktop.
 */
export function probeCitrine(
  url: string = CITRINE_RELAY_URL,
  timeoutMs = 3000,
): Promise<boolean> {
  if (!isCitrinePossible()) return Promise.resolve(false);

  return new Promise<boolean>((resolve) => {
    let settled = false;
    let ws: WebSocket | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const done = (result: boolean) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      try {
        ws?.close();
      } catch {
        // ignore close errors
      }
      resolve(result);
    };

    try {
      ws = new WebSocket(url);
    } catch {
      resolve(false);
      return;
    }

    // Unique, throwaway subscription id we can match in the reply.
    const subId = `citrine-probe-${Math.random().toString(36).slice(2, 10)}`;

    timer = setTimeout(() => done(false), timeoutMs);

    ws.onopen = () => {
      try {
        // Ask for a single event of any kind — bounded and cheap. We don't care
        // about the payload, only that the peer answers in the Nostr protocol.
        ws!.send(JSON.stringify(['REQ', subId, { limit: 1 }]));
      } catch {
        done(false);
      }
    };

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
        // A Nostr relay replies with a JSON array whose first element is a known
        // verb. EVENT/EOSE/CLOSED echo our subId; NOTICE is a generic message.
        if (Array.isArray(data) && typeof data[0] === 'string') {
          const verb = data[0];
          const matchesSub = data[1] === subId;
          if (
            ((verb === 'EVENT' || verb === 'EOSE' || verb === 'CLOSED') && matchesSub) ||
            verb === 'NOTICE'
          ) {
            done(true);
            return;
          }
        }
        // Any other shape: not a Nostr relay we recognise — keep waiting until
        // timeout (a valid relay will still send EOSE).
      } catch {
        // Non-JSON frame → not a Nostr relay; wait for timeout.
      }
    };

    ws.onerror = () => done(false);
    ws.onclose = () => done(false);
  });
}
